import { ReactNode, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { CircularProgress, Box, Typography } from "@mui/material";
import {
  selectSelectedWorkspace,
  selectAvailableWorkspaces,
  selectWorkspaceLoading,
  initializeWorkspaces,
  initializeWorkspaceData,
  setSelectedWorkspace,
} from "../../features/workspaceSlice";
import { selectUserData } from "../../features/userSlice";
import { useDeviceDetection } from "../../customHooks/useDeviceDetection";

interface WorkspaceGuardProps {
  children: ReactNode;
}

export const WorkspaceGuard = ({ children }: WorkspaceGuardProps) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { deviceType, isDetecting } = useDeviceDetection();
  const [isInitializing, setIsInitializing] = useState(true);

  const selectedWorkspace = useSelector(selectSelectedWorkspace);
  const availableWorkspaces = useSelector(selectAvailableWorkspaces);
  const isLoading = useSelector(selectWorkspaceLoading);
  const userData = useSelector(selectUserData);

  // Environment detection
  const environment = import.meta.env.VITE_ENV;
  const isProduction = environment === "production";
  const isStaging = environment === "staging" || environment === "development";
  const isDesktop = deviceType === "desktop";

  // Determine if workspace should be disabled
  const shouldDisableWorkspace =
    (isProduction && isDesktop) || (isStaging && isDesktop);

  // Get workspace UUID from URL
  const workspaceIdFromUrl = searchParams.get("workspace");

  // EFFECT 1: Initialize workspaces from user data (only once)
  useEffect(() => {
    if (userData?.data && availableWorkspaces.length === 0) {
      dispatch(initializeWorkspaces(userData.data));
      dispatch(initializeWorkspaceData(userData.data));
    }
  }, [userData, availableWorkspaces.length, dispatch]);

  // EFFECT 2: Handle workspace selection logic
  useEffect(() => {
    // Wait for workspaces to load
    if (availableWorkspaces.length === 0) {
      return;
    }

    // If workspace is disabled, remove query param and select first workspace silently
    if (shouldDisableWorkspace) {
      // Remove workspace from URL if it exists
      if (workspaceIdFromUrl) {
        searchParams.delete("workspace");
        setSearchParams(searchParams, { replace: true });
      }

      // Select first workspace if none selected (but don't add to URL)
      if (!selectedWorkspace) {
        const firstWorkspace = availableWorkspaces[0];
        dispatch(setSelectedWorkspace(firstWorkspace));
      }

      // Mark initialization as complete
      setIsInitializing(false);
      return;
    }

    // Normal workspace behavior when not disabled
    // Case 1: Workspace ID exists in URL
    if (workspaceIdFromUrl) {
      const workspaceFromUrl = availableWorkspaces.find(
        (w: any) => w.id === workspaceIdFromUrl,
      );

      if (workspaceFromUrl) {
        // Valid workspace in URL - select it if not already selected
        if (selectedWorkspace?.id !== workspaceFromUrl.id) {
          dispatch(setSelectedWorkspace(workspaceFromUrl));
        }
      } else {
        // Invalid workspace ID in URL - remove it and select first
        searchParams.delete("workspace");
        setSearchParams(searchParams, { replace: true });

        if (!selectedWorkspace) {
          const firstWorkspace = availableWorkspaces[0];
          dispatch(setSelectedWorkspace(firstWorkspace));
          searchParams.set("workspace", firstWorkspace.id);
          setSearchParams(searchParams, { replace: true });
        }
      }

      // Mark initialization as complete
      setIsInitializing(false);
      return;
    }

    // Case 2: No workspace in URL and no workspace selected
    if (!selectedWorkspace) {
      const firstWorkspace = availableWorkspaces[0];
      dispatch(setSelectedWorkspace(firstWorkspace));
      searchParams.set("workspace", firstWorkspace.id);
      setSearchParams(searchParams, { replace: true });

      // Mark initialization as complete
      setIsInitializing(false);
      return;
    }

    // Case 3: Workspace is selected but not in URL
    if (selectedWorkspace && !workspaceIdFromUrl) {
      searchParams.set("workspace", selectedWorkspace.id);
      setSearchParams(searchParams, { replace: true });
    }

    // Mark initialization as complete
    setIsInitializing(false);
  }, [
    availableWorkspaces,
    selectedWorkspace,
    workspaceIdFromUrl,
    shouldDisableWorkspace,
    dispatch,
    searchParams,
    setSearchParams,
  ]);

  // Show loading while user data is loading, workspace is loading, device is detecting, or initializing
  if (!userData?.data || isLoading || isDetecting || isInitializing) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
          gap: 3,
        }}
      >
        <CircularProgress size={60} thickness={4} color="secondary" />
        <Typography variant="h6" color="textSecondary">
          Initializing Aladdin...
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Please wait
        </Typography>
      </Box>
    );
  }

  // No workspaces available (only show this after initialization is complete)
  if (availableWorkspaces.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h5" color="error">
          No Workspaces Available
        </Typography>
        <Typography variant="body1" color="textSecondary" textAlign="center">
          You don't have access to any workspaces. Please contact your
          administrator.
        </Typography>
      </Box>
    );
  }

  // Don't render children until workspace is selected
  if (!selectedWorkspace) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="textSecondary">
          Selecting workspace...
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};
